import { Alert, Box, Button, CircularProgress, Snackbar, Switch, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePurchaseManager } from "../_stubs/usePurchaseManager";
import PurchaseManager from "../_stubs/PurchaseManager";
import { useSubscription } from "../lib/SubscriptionContext";
import DataDeletionRequestDialog from "../../../../pages/profile/DataDeletionRequestDialog";
import UserProfileService from "../../../../pages/profile/UserProfileService";
import { usePagesTranslation } from "../../../../pages/locales";
import AuthService, { AUTH_CHANGED_EVENT } from "../../../../services/AuthService";
import Logger from "../../../../services/Logger";
import PushNotificationService from "../_stubs/PushNotificationService";
import UserService from "../../../../services/UserService";
import { getIapResultUserMessage } from "../_stubs/iapFeedbackMessages";
import {
    collectIapRuntimeReport,
    getIapSettingsSnapshot,
    isIapDebugEnabled,
    printIapRuntimeReport,
    printIapSettings,
} from "../_stubs/iapDebugLog";
import { getLastRevenueCatOfferingsError } from "../_stubs/revenueCatPurchase";
import DeleteAccountConfirmDialog from "../components/DeleteAccountConfirmDialog";
import AppearancePicker from "../components/AppearancePicker";
import CreditPurchaseSection from "../components/CreditPurchaseSection";
import HintPointsPurchaseSection from "../components/HintPointsPurchaseSection";
import MobileAppLegalLinks from "../components/MobileAppLegalLinks";
import { APP_SITE_ID } from "../../../../config/appSiteLinks";
import { GroupedList, GroupedRow, GroupedSection, systemFont } from "../components/IOSGroupedList";
import IOSChevronButton from "../components/IOSChevronButton";
import PageShell from "../components/PageShell";
import { useSlidingAppearance } from "../lib/AppearanceContext";
import { slidingPageContentSx } from "../lib/appearance";
import { loadPlayRouteState, navigateToPlay } from "../lib/playRouteState";
import {
    ACCOUNT_DELETION_CONFIRMED_PATH,
    MAIN_PATH,
    MY_PUZZLES_PATH,
    PLAY_PATH,
    SETTINGS_PATH,
} from "../routes";

import { LOW_CREDIT_TOP_UP_THRESHOLD } from "../lib/creditConstants";
import { getPremiumBenefitLines } from "../lib/premiumBenefits";
import SlidingWalletSummary from "../components/SlidingWalletSummary";
import {
    loadBgmAutoPlayPreference,
    loadPlaySoundEffectsPreference,
    loadShowPlayTimerPreference,
    resolveBgmAutoPlayPreference,
    resolvePlaySoundEffectsPreference,
    saveBgmAutoPlayPreference,
    savePlaySoundEffectsPreference,
    saveShowPlayTimerPreference,
} from "../lib/storage";
import { getHomePremium } from "../styles/homePremiumStyles";
import { settingsPrimaryButtonSx } from "../styles/premiumCtaStyles";
import { resolveSlidingQuestCoins } from "../../../../services/PuzzleCoinsUtil";

export default function SettingsPage() {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { tokens, isDark } = useSlidingAppearance();
    const home = getHomePremium(isDark);
    const accent = home.teal;
    const iap = usePurchaseManager();
    const { hasStandardPlan, hasLegacyUnlimitedAccess, credits, hintPoints, purchaseCredits, purchaseHintPoints, notifyAccessRefresh, access, iap: subscriptionIap } =
        useSubscription();
    const [inlineError, setInlineError] = useState("");
    const [purchaseNeedsAppSignIn, setPurchaseNeedsAppSignIn] = useState(false);
    const [accountLoading, setAccountLoading] = useState(true);
    const [showPlayTimer, setShowPlayTimer] = useState(() => loadShowPlayTimerPreference());
    const [bgmAutoPlay, setBgmAutoPlay] = useState(() => resolveBgmAutoPlayPreference(true));
    const [playSoundEffects, setPlaySoundEffects] = useState(() =>
        resolvePlaySoundEffectsPreference(true),
    );
    const [puzzleScore, setPuzzleScore] = useState(0);

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [accountEmail, setAccountEmail] = useState("");
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [dataDeletionDialogOpen, setDataDeletionDialogOpen] = useState(false);
    const [dataDeletionSubmitting, setDataDeletionSubmitting] = useState(false);
    const [iapDebugReport, setIapDebugReport] = useState(null);
    const [iapDebugBusy, setIapDebugBusy] = useState(false);
    const [cloudUserId, setCloudUserId] = useState(null);
    const [notificationsEnabled, setNotificationsEnabled] = useState(true);
    const [notificationsSaving, setNotificationsSaving] = useState(false);
    const showIapDebug = isIapDebugEnabled();
    const showPushSettings = PushNotificationService.isPushSupported();

    const loadAccount = useCallback(async () => {
        setAccountLoading(true);
        try {
            const authed = await AuthService.isAuthenticated();
            setIsAuthenticated(authed);
            if (!authed) {
                setAccountEmail("");
                setCloudUserId(null);
                setNotificationsEnabled(true);
                setPuzzleScore(0);
                setBgmAutoPlay(resolveBgmAutoPlayPreference(true));
                setPlaySoundEffects(resolvePlaySoundEffectsPreference(true));
                return;
            }
            const userInfo = await UserService.getUserInfo();
            const email = userInfo?.cloudUser?.email;
            setAccountEmail(typeof email === "string" ? email.trim() : "");
            const userId = userInfo?.cloudUser?.id;
            setCloudUserId(typeof userId === "string" && userId.trim() ? userId.trim() : null);
            const byApp = userInfo?.cloudUser?.details?.notificationsByApp;
            setNotificationsEnabled(
                byApp?.sliding_puzzle_quest === undefined || byApp?.sliding_puzzle_quest === null
                    ? true
                    : Boolean(byApp.sliding_puzzle_quest)
            );
            setPuzzleScore(resolveSlidingQuestCoins(userInfo?.cloudUser));
            const settings = UserService.getSettingsFormData(userInfo);
            if (loadBgmAutoPlayPreference() === null) {
                setBgmAutoPlay(Boolean(settings.bgmAutoPlay));
            } else {
                setBgmAutoPlay(resolveBgmAutoPlayPreference(settings.bgmAutoPlay));
            }
            if (loadPlaySoundEffectsPreference() === null) {
                setPlaySoundEffects(settings.soundEnabled !== false);
            } else {
                setPlaySoundEffects(resolvePlaySoundEffectsPreference(settings.soundEnabled !== false));
            }
        } catch (e) {
            Logger.error("SettingsPage loadAccount", e);
            setIsAuthenticated(false);
            setAccountEmail("");
            setCloudUserId(null);
            setNotificationsEnabled(true);
            setPuzzleScore(0);
        } finally {
            setAccountLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadAccount();
        const onAuth = () => void loadAccount();
        window.addEventListener(AUTH_CHANGED_EVENT, onAuth);
        return () => window.removeEventListener(AUTH_CHANGED_EVENT, onAuth);
    }, [loadAccount]);

    useEffect(() => {
        void credits.refreshBalance();
        void hintPoints.refreshBalance();
    }, [credits.refreshBalance, hintPoints.refreshBalance]);

    useEffect(() => {
        void hintPoints.refreshPackage();
    }, [hintPoints.refreshPackage]);

    const runIapDebugReport = async () => {
        setIapDebugBusy(true);
        try {
            printIapSettings();
            const report = await collectIapRuntimeReport();
            setIapDebugReport(report);
            await printIapRuntimeReport();
        } finally {
            setIapDebugBusy(false);
        }
    };

    useEffect(() => {
        if (!showIapDebug) return undefined;
        void runIapDebugReport();
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- run once when debug panel mounts
    }, [showIapDebug]);

    const purchaseBusy = iap.isProcessing || credits.isProcessing || hintPoints.isProcessing;
    const creditPurchaseLocked = hintPoints.isProcessing || iap.isProcessing;
    const hintPointsPurchaseLocked = credits.isProcessing || iap.isProcessing;

    const premiumBenefits = getPremiumBenefitLines(t);
    const legacyPremium =
        hasLegacyUnlimitedAccess || access.hasStandardPlan || access.isPremium || subscriptionIap.isPremium;
    const showLowCreditTopUp =
        !credits.balanceLoading &&
        credits.creditBalance > 0 &&
        credits.creditBalance < LOW_CREDIT_TOP_UP_THRESHOLD;
    /** Credit wallet UI — always show packs; signed-in users get Pocket/Pro top-up. */
    const showCreditPurchase = true;
    const creditPurchaseMode = isAuthenticated ? "topup" : "intro";

    const goSignIn = () => {
        navigate("/login", { state: { from: SETTINGS_PATH } });
    };

    const handleShowPlayTimerChange = (event) => {
        const enabled = Boolean(event.target.checked);
        setShowPlayTimer(enabled);
        saveShowPlayTimerPreference(enabled);
    };

    const persistCloudAudioPrefs = useCallback(async (patch) => {
        try {
            const userInfo = await UserService.getUserInfo();
            const id = userInfo?.cloudUser?.id;
            if (!id) return;
            await UserService.updateUserProfile(id, {
                ...UserService.getSettingsFormData(userInfo),
                ...patch,
            });
        } catch (e) {
            Logger.warn("SettingsPage persistCloudAudioPrefs", e);
        }
    }, []);

    const handleBgmAutoPlayChange = (event) => {
        const enabled = Boolean(event.target.checked);
        setBgmAutoPlay(enabled);
        saveBgmAutoPlayPreference(enabled);
        if (isAuthenticated) {
            void persistCloudAudioPrefs({ bgmAutoPlay: enabled });
        }
    };

    const handlePlaySoundEffectsChange = (event) => {
        const enabled = Boolean(event.target.checked);
        setPlaySoundEffects(enabled);
        savePlaySoundEffectsPreference(enabled);
        if (isAuthenticated) {
            void persistCloudAudioPrefs({ soundEnabled: enabled });
        }
    };

    const handleNotificationsChange = async (event) => {
        const enabled = event.target.checked;
        if (!isAuthenticated || !cloudUserId) {
            setInlineError(
                t("native.settings.signInPrompt", {
                    defaultValue: "Sign in to save progress and manage your account.",
                }),
            );
            return;
        }

        const previous = notificationsEnabled;
        setNotificationsEnabled(enabled);
        setNotificationsSaving(true);
        setInlineError("");

        try {
            await UserService.updateUserProfile(
                cloudUserId,
                { notificationsByApp: { sliding_puzzle_quest: enabled }, notifications: null }
            );
            if (enabled) {
                const result = await PushNotificationService.enablePushNotifications();
                if (!result.ok) {
                    setNotificationsEnabled(false);
                    await UserService.updateUserProfile(
                        cloudUserId,
                        { notificationsByApp: { sliding_puzzle_quest: false }, notifications: null }
                    );
                    if (result.reason === "denied") {
                        setInlineError(
                            t("native.settings.notificationsDenied", {
                                defaultValue:
                                    "Notifications are off in iOS Settings. Enable them there to get alerts for new puzzles.",
                            }),
                        );
                    }
                }
            } else {
                await PushNotificationService.disablePushNotifications();
            }
        } catch (e) {
            Logger.error("SettingsPage notifications", e);
            setNotificationsEnabled(previous);
            setInlineError(
                t("native.settings.notificationsSaveFailed", {
                    defaultValue: "Could not update notification settings. Try again.",
                }),
            );
        } finally {
            setNotificationsSaving(false);
        }
    };

    const finishPendingBackendSync = useCallback(async () => {
        const sync = await PurchaseManager.completePendingIapBackendSync();
        if (!sync) {
            return false;
        }
        if (sync.cancelled) {
            return false;
        }
        if (sync.success) {
            notifyAccessRefresh();
            setInlineError("");
            return true;
        }
        const msg = getIapResultUserMessage(sync, t, "restore");
        if (msg) {
            setInlineError(msg);
        }
        return false;
    }, [notifyAccessRefresh, t]);

    useEffect(() => {
        if (!showCreditPurchase) {
            return;
        }
        void credits.refreshPackages();
    }, [showCreditPurchase, credits.refreshPackages]);

    useEffect(() => {
        if (!isAuthenticated || hasStandardPlan || purchaseBusy) {
            return;
        }
        if (!PurchaseManager.hasPendingIapBackendSync()) {
            return;
        }
        void finishPendingBackendSync();
    }, [isAuthenticated, hasStandardPlan, purchaseBusy, finishPendingBackendSync]);

    const applyCreditPurchaseResult = async (result) => {
        setPurchaseNeedsAppSignIn(false);
        if (result?.cancelled) {
            return;
        }
        if (result?.success && result?.pendingBackendSync) {
            notifyAccessRefresh();
            if (isAuthenticated) {
                const synced = await PurchaseManager.completePendingCreditBackendSync({
                    forceRecoverFromRevenueCat: true,
                });
                if (!synced?.success) {
                    setInlineError(
                        t("native.credits.signInAfterPurchase", {
                            defaultValue:
                                "Purchase recorded. Sign in to sync credits to your account.",
                        }),
                    );
                } else {
                    setInlineError("");
                }
                return;
            }
            setPurchaseNeedsAppSignIn(true);
            setInlineError("");
            return;
        }
        if (result?.success) {
            await credits.refreshBalance();
            notifyAccessRefresh();
            setInlineError("");
            return;
        }
        const recovered = await PurchaseManager.completePendingCreditBackendSync({
            forceRecoverFromRevenueCat: true,
        });
        if (recovered?.success) {
            await credits.refreshBalance();
            notifyAccessRefresh();
            setInlineError("");
            return;
        }
        if (result?.error === "login_required") {
            if (isAuthenticated) {
                const synced = await PurchaseManager.completePendingCreditBackendSync({
                    forceRecoverFromRevenueCat: true,
                });
                if (!synced?.success) {
                    setInlineError(t("native.credits.signInAfterPurchase"));
                }
                return;
            }
            setPurchaseNeedsAppSignIn(true);
            setInlineError(t("native.credits.signInAfterPurchase"));
            return;
        }
        const msg = getIapResultUserMessage(result, t, "purchase");
        if (msg) {
            setInlineError(msg);
        }
    };

    const runPurchase = async (packageKey) => {
        if (purchaseBusy) return;
        setPurchaseNeedsAppSignIn(false);
        setInlineError("");
        const result = await purchaseCredits(packageKey);
        await applyCreditPurchaseResult(result);
    };

    const applyHintPointsPurchaseResult = async (result) => {
        if (result?.cancelled) {
            return;
        }
        if (result?.success && result?.pendingBackendSync) {
            notifyAccessRefresh();
            if (!isAuthenticated) {
                setPurchaseNeedsAppSignIn(true);
            }
            setInlineError(
                t("native.hintPoints.signInAfterPurchase", {
                    defaultValue: "Purchase recorded. Sign in to sync assistant coins to your account.",
                }),
            );
            return;
        }
        if (result?.success) {
            await hintPoints.refreshBalance();
            notifyAccessRefresh();
            setInlineError("");
            return;
        }
        const msg = getIapResultUserMessage(result, t, "purchase");
        if (msg) {
            setInlineError(msg);
        }
    };

    const runHintPointsPurchase = async () => {
        if (purchaseBusy) return;
        setInlineError("");
        const result = await purchaseHintPoints();
        await applyHintPointsPurchaseResult(result);
    };

    const showRestorePurchases = iap.isIapAvailable || credits.isIapAvailable;

    const handleRestorePurchases = async () => {
        if (purchaseBusy) {
            return;
        }
        setInlineError("");
        const result = await iap.restore();
        if (result?.success) {
            notifyAccessRefresh();
            if (isAuthenticated) {
                const creditSync = await PurchaseManager.completePendingCreditBackendSync({
                    forceRecoverFromRevenueCat: true,
                });
                if (creditSync?.success) {
                    notifyAccessRefresh();
                    setInlineError("");
                }
            }
            return;
        }
        if (!result?.cancelled) {
            const msg = getIapResultUserMessage(result, t, "restore");
            if (msg) {
                setInlineError(msg);
            }
        }
    };

    const subscriptionFooter = credits.isIapAvailable
        ? t("native.credits.consumableDisclosure", {
              defaultValue:
                  "Credits are consumable in-app purchases. They do not auto-renew. Unused credits remain in your account until used.",
          })
        : t("subs.purchase.not-available", {
              defaultValue: "In-app purchases are available in the iOS app.",
          });

    const handleSignOut = async () => {
        try {
            await AuthService.logout();
            navigate(MAIN_PATH, { replace: true });
        } catch (e) {
            Logger.error("SettingsPage sign out", e);
        }
    };

    const handleDataDeletionRequest = async () => {
        setDataDeletionSubmitting(true);
        try {
            return await UserProfileService.requestDataDeletion();
        } finally {
            setDataDeletionSubmitting(false);
        }
    };

    const handleBack = useCallback(() => {
        const fromState = location.state && typeof location.state === "object" ? location.state : {};
        if (fromState.returnToPlay) {
            const playState =
                (fromState.playState && typeof fromState.playState === "object"
                    ? fromState.playState
                    : null) || loadPlayRouteState();
            if (playState && typeof playState === "object") {
                navigateToPlay(navigate, PLAY_PATH, {
                    ...playState,
                    resume: true,
                });
                return;
            }
            navigate(-1);
            return;
        }
        if (typeof fromState.returnPath === "string" && fromState.returnPath.trim().startsWith("/")) {
            navigate(fromState.returnPath.trim());
            return;
        }
        navigate(MY_PUZZLES_PATH);
    }, [location.state, navigate]);

    return (
        <PageShell
            showBrandHeader={false}
            pageHero="settings"
            contentColumn={false}
            pageTitle={t("native.settings.screenTitle", { defaultValue: "SETTINGS" })}
            leading={
                <IOSChevronButton
                    direction="left"
                    color={tokens.text}
                    ariaLabel={t("native.settings.back", { defaultValue: "Back" })}
                    onClick={handleBack}
                />
            }
        >
            <Box
                sx={{
                    ...slidingPageContentSx(tokens),
                    bgcolor: "transparent",
                    background: "transparent",
                }}
            >
            <GroupedList>
                <GroupedSection
                    footer={subscriptionFooter}
                >
                    <GroupedRow>
                        {!hasStandardPlan ? (
                            <Typography
                                sx={{
                                    fontFamily: systemFont,
                                    fontSize: "0.9375rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.02em",
                                    color: tokens.text,
                                    mb: 1.25,
                                }}
                            >
                                {t("native.settings.accountStatusFree", {
                                    defaultValue: "ACCOUNT STATUS: FREE",
                                })}
                            </Typography>
                        ) : null}
                        {!hasStandardPlan ? (
                            <Box component="ul" sx={{ m: 0, pl: 2.25, mb: 1.5 }}>
                                {premiumBenefits.map((line) => (
                                    <Typography
                                        key={line}
                                        component="li"
                                        sx={{
                                            fontFamily: systemFont,
                                            fontSize: "0.9375rem",
                                            lineHeight: 1.5,
                                            color: tokens.textMuted,
                                            mb: 0.5,
                                        }}
                                    >
                                        {line}
                                    </Typography>
                                ))}
                            </Box>
                        ) : null}

                        {isAuthenticated ? (
                            <Box sx={{ mb: 1.5 }}>
                                <SlidingWalletSummary
                                    creditBalance={credits.creditBalance}
                                    hintPointBalance={hintPoints.pointBalance}
                                    loading={
                                        accountLoading ||
                                        credits.balanceLoading ||
                                        hintPoints.balanceLoading
                                    }
                                />
                            </Box>
                        ) : (
                            <Typography
                                sx={{
                                    fontFamily: systemFont,
                                    fontSize: "0.875rem",
                                    color: tokens.textMuted,
                                    mb: 1.5,
                                    lineHeight: 1.45,
                                }}
                            >
                                {t("native.credits.signInForWallet", {
                                    defaultValue: "Sign in to view your credits and assistant coins.",
                                })}{" "}
                                <Button
                                    type="button"
                                    onClick={goSignIn}
                                    sx={{
                                        textTransform: "none",
                                        fontFamily: systemFont,
                                        fontSize: "0.875rem",
                                        p: 0,
                                        minWidth: 0,
                                        verticalAlign: "baseline",
                                        color: accent,
                                    }}
                                >
                                    {t("login.sign-in", { defaultValue: "Sign in" })}
                                </Button>
                            </Typography>
                        )}

                        {inlineError ? (
                            <Typography
                                sx={{
                                    fontFamily: systemFont,
                                    fontSize: "0.8125rem",
                                    color: tokens.error,
                                    mb: 1.5,
                                    lineHeight: 1.45,
                                }}
                            >
                                {inlineError}
                                {!isAuthenticated && purchaseNeedsAppSignIn ? (
                                    <>
                                        {" "}
                                        <Button
                                            type="button"
                                            onClick={goSignIn}
                                            sx={{
                                                textTransform: "none",
                                                fontFamily: systemFont,
                                                fontSize: "0.8125rem",
                                                p: 0,
                                                minWidth: 0,
                                                verticalAlign: "baseline",
                                                color: accent,
                                            }}
                                        >
                                            {t("login.sign-in", { defaultValue: "Sign in" })}
                                        </Button>
                                    </>
                                ) : null}
                            </Typography>
                        ) : null}

                        {hasStandardPlan && credits.creditBalance > 0 ? (
                            <Typography
                                sx={{
                                    fontFamily: systemFont,
                                    fontSize: "0.9375rem",
                                    fontWeight: 600,
                                    color: accent,
                                    mb: 0.5,
                                }}
                            >
                                {t("native.credits.balance", {
                                    defaultValue: "{{count}} credits remaining",
                                    count: credits.creditBalance,
                                })}
                            </Typography>
                        ) : null}

                        {hasStandardPlan && credits.creditBalance > 0 ? (
                            <Typography
                                sx={{
                                    fontFamily: systemFont,
                                    fontSize: "0.8125rem",
                                    fontWeight: 400,
                                    color: tokens.textMuted,
                                    mb: 1.5,
                                    lineHeight: 1.45,
                                }}
                            >
                                {t("native.credits.freeEasyNoCreditCharge", {
                                    defaultValue: "4×4 Easy puzzles don't use credits.",
                                })}
                            </Typography>
                        ) : null}

                        {legacyPremium && credits.creditBalance <= 0 ? (
                            <Typography
                                sx={{
                                    fontFamily: systemFont,
                                    fontSize: "0.9375rem",
                                    fontWeight: 600,
                                    color: accent,
                                    mb: 1.5,
                                }}
                            >
                                {t("native.settings.premiumActive", {
                                    defaultValue: "Premium is active on this account.",
                                })}
                            </Typography>
                        ) : null}

                        {showCreditPurchase && credits.creditBalance <= 0 && !legacyPremium ? (
                            <Typography
                                sx={{
                                    fontFamily: systemFont,
                                    fontSize: "0.9375rem",
                                    fontWeight: 600,
                                    color: tokens.warning,
                                    mb: 1.5,
                                }}
                            >
                                {t("native.credits.outOfCredits", {
                                    defaultValue: "You're out of credits. Add a pack to keep playing.",
                                })}
                            </Typography>
                        ) : null}

                        {showLowCreditTopUp ? (
                            <Typography
                                sx={{
                                    fontFamily: systemFont,
                                    fontSize: "0.875rem",
                                    fontWeight: 500,
                                    color: tokens.warning,
                                    mb: 1.5,
                                    lineHeight: 1.45,
                                }}
                            >
                                {t("native.credits.lowCreditsTopUp", {
                                    defaultValue:
                                        "You're running low on credits. Add a Pocket or Pro pack to keep playing premium puzzles.",
                                    count: credits.creditBalance,
                                    threshold: LOW_CREDIT_TOP_UP_THRESHOLD,
                                })}
                            </Typography>
                        ) : null}

                        {showCreditPurchase ? (
                            <CreditPurchaseSection
                                mode={creditPurchaseMode}
                                creditBalance={credits.creditBalance}
                                balanceLoading={credits.balanceLoading}
                                packagesLoading={credits.packagesLoading}
                                isProcessing={credits.isProcessing}
                                purchaseLocked={creditPurchaseLocked}
                                isIapAvailable={credits.isIapAvailable}
                                showBalanceDivider={
                                    hasStandardPlan && credits.creditBalance > 0
                                }
                                onPurchase={runPurchase}
                                getPackagePrice={credits.getPackagePrice}
                                getCreditsForPackage={credits.getCreditsForPackage}
                            />
                        ) : null}

                        <HintPointsPurchaseSection
                            pointBalance={hintPoints.pointBalance}
                            balanceLoading={hintPoints.balanceLoading}
                            packAmount={hintPoints.packAmount}
                            packPrice={hintPoints.getPackPrice()}
                            isProcessing={hintPoints.isProcessing}
                            purchaseLocked={hintPointsPurchaseLocked}
                            isIapAvailable={hintPoints.isIapAvailable}
                            onPurchase={runHintPointsPurchase}
                        />

                        {showRestorePurchases ? (
                            <Button
                                type="button"
                                fullWidth
                                disabled={purchaseBusy}
                                onClick={() => void handleRestorePurchases()}
                                sx={{
                                    fontFamily: systemFont,
                                    textTransform: "none",
                                    fontSize: "1rem",
                                    fontWeight: 500,
                                    color: accent,
                                    justifyContent: "center",
                                    py: 1.25,
                                    minHeight: 44,
                                    mt: 2,
                                    pt: 2,
                                    borderTop: `1px solid ${tokens.divider}`,
                                }}
                            >
                                {purchaseBusy ? (
                                    <CircularProgress size={22} sx={{ color: accent }} />
                                ) : (
                                    t("native.profile.restorePurchases", {
                                        defaultValue: "Restore purchases",
                                    })
                                )}
                            </Button>
                        ) : null}
                    </GroupedRow>
                </GroupedSection>

                <GroupedSection
                    title={t("native.settings.scoreSection", { defaultValue: "Score" })}
                    footer={
                        isAuthenticated
                            ? t("native.settings.scoreFooter", {
                                  defaultValue:
                                      "Points earned for completing puzzles. Harder grids earn more.",
                              })
                            : t("native.settings.scoreSignInFooter", {
                                  defaultValue: "Sign in to earn and track your puzzle score.",
                              })
                    }
                >
                    <GroupedRow last>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontFamily: systemFont,
                                        fontSize: "0.9375rem",
                                        fontWeight: 600,
                                        color: tokens.text,
                                    }}
                                >
                                    {t("native.settings.currentScore", {
                                        defaultValue: "Current score",
                                    })}
                                </Typography>
                                {!isAuthenticated ? (
                                    <Typography
                                        sx={{
                                            fontFamily: systemFont,
                                            fontSize: "0.8125rem",
                                            lineHeight: 1.45,
                                            color: tokens.textMuted,
                                            mt: 0.5,
                                        }}
                                    >
                                        {t("native.settings.scoreSignInHint", {
                                            defaultValue:
                                                "Complete puzzles while signed in to add score.",
                                        })}
                                    </Typography>
                                ) : null}
                            </Box>
                            <Typography
                                sx={{
                                    fontFamily: systemFont,
                                    fontSize: "1.25rem",
                                    fontWeight: 800,
                                    fontVariantNumeric: "tabular-nums",
                                    color: tokens.text,
                                    flexShrink: 0,
                                }}
                                aria-label={t("native.settings.currentScore", {
                                    defaultValue: "Current score",
                                })}
                            >
                                {accountLoading ? "—" : puzzleScore.toLocaleString()}
                            </Typography>
                        </Box>
                    </GroupedRow>
                </GroupedSection>

                {accountLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={28} sx={{ color: accent }} />
                    </Box>
                ) : isAuthenticated ? (
                    <>
                        <GroupedSection
                            title={t("native.settings.accountSection", { defaultValue: "Account" })}
                        >
                            <GroupedRow>
                                <Typography
                                    sx={{
                                        fontFamily: systemFont,
                                        fontSize: "0.8125rem",
                                        color: tokens.textSubtle,
                                        mb: 0.35,
                                    }}
                                >
                                    {t("profile.profileInfo.email", { defaultValue: "Email" })}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: systemFont,
                                        fontSize: "1rem",
                                        fontWeight: 500,
                                        wordBreak: "break-all",
                                        color: tokens.text,
                                    }}
                                >
                                    {accountEmail || "—"}
                                </Typography>
                            </GroupedRow>
                            <GroupedRow last>
                                <Button
                                    type="button"
                                    fullWidth
                                    onClick={() => void handleSignOut()}
                                    sx={{
                                        fontFamily: systemFont,
                                        textTransform: "none",
                                        fontSize: "1rem",
                                        fontWeight: 500,
                                        color: accent,
                                        justifyContent: "flex-start",
                                        px: 0,
                                        minHeight: 44,
                                    }}
                                >
                                    {t("nav.sign-out", { defaultValue: "Sign out" })}
                                </Button>
                            </GroupedRow>
                        </GroupedSection>

                        <GroupedSection
                            title={t("native.settings.accountActions", {
                                defaultValue: "Account Actions",
                            })}
                        >
                            <GroupedRow last>
                                <Button
                                    type="button"
                                    fullWidth
                                    onClick={() => setDeleteConfirmOpen(true)}
                                    sx={{
                                        fontFamily: systemFont,
                                        textTransform: "none",
                                        fontSize: "1rem",
                                        fontWeight: 600,
                                        color: "#dc2626",
                                        justifyContent: "center",
                                        py: 1.25,
                                        minHeight: 48,
                                        borderRadius: "8px",
                                        bgcolor: "rgba(220, 38, 38, 0.08)",
                                        "&:hover": { bgcolor: "rgba(220, 38, 38, 0.14)" },
                                    }}
                                >
                                    {t("native.settings.deleteAccount", {
                                        defaultValue: "Delete Account",
                                    })}
                                </Button>
                            </GroupedRow>
                        </GroupedSection>
                    </>
                ) : (
                    <GroupedSection
                        title={t("native.settings.accountSection", { defaultValue: "Account" })}
                    >
                        <GroupedRow last>
                            <Typography
                                sx={{
                                    fontFamily: systemFont,
                                    fontSize: "0.9375rem",
                                    lineHeight: 1.45,
                                    color: tokens.textMuted,
                                    mb: 1.5,
                                }}
                            >
                                {t("native.settings.signInPrompt", {
                                    defaultValue: "Sign in to save progress and manage your account.",
                                })}
                            </Typography>
                            <Button
                                type="button"
                                fullWidth
                                onClick={goSignIn}
                                sx={settingsPrimaryButtonSx({ animate: true, accent })}
                            >
                                <Box component="span">
                                    {t("native.settings.signInButton", { defaultValue: "Sign In" })}
                                </Box>
                            </Button>
                        </GroupedRow>
                    </GroupedSection>
                )}

                {showIapDebug ? (
                    <GroupedSection title="IAP debug (console)">
                        <GroupedRow last>
                            <Typography
                                sx={{
                                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                    fontSize: "0.6875rem",
                                    lineHeight: 1.45,
                                    color: tokens.textMuted,
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-all",
                                    mb: 1.5,
                                }}
                            >
                                {JSON.stringify(
                                    iapDebugReport ?? {
                                        settings: getIapSettingsSnapshot(),
                                        lastOfferingsError: getLastRevenueCatOfferingsError(),
                                    },
                                    null,
                                    2,
                                )}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: systemFont,
                                    fontSize: "0.8125rem",
                                    color: tokens.textMuted,
                                    mb: 1.5,
                                    lineHeight: 1.45,
                                }}
                            >
                                Also open Safari Web Inspector and run{" "}
                                <Box component="span" sx={{ fontFamily: "monospace" }}>
                                    __PUZZLE_IAP_DEBUG__.printReport()
                                </Box>
                            </Typography>
                            <Button
                                type="button"
                                fullWidth
                                disabled={iapDebugBusy}
                                onClick={() => void runIapDebugReport()}
                                sx={{
                                    textTransform: "none",
                                    fontFamily: systemFont,
                                    minHeight: 44,
                                }}
                            >
                                {iapDebugBusy ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    "Refresh & print IAP logs"
                                )}
                            </Button>
                        </GroupedRow>
                    </GroupedSection>
                ) : null}

                <GroupedSection
                    title={t("native.settings.legalSection", { defaultValue: "Legal" })}
                >
                    <MobileAppLegalLinks appId={APP_SITE_ID.SLIDING_PUZZLE_QUEST} />
                </GroupedSection>

                <GroupedSection
                    title={t("native.settings.preferencesSection", { defaultValue: "Preferences" })}
                >
                    <GroupedRow>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontFamily: systemFont,
                                        fontSize: "0.9375rem",
                                        fontWeight: 600,
                                        color: tokens.text,
                                    }}
                                >
                                    {t("native.settings.showTimer", { defaultValue: "Show timer" })}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: systemFont,
                                        fontSize: "0.8125rem",
                                        lineHeight: 1.45,
                                        color: tokens.textMuted,
                                        mt: 0.5,
                                    }}
                                >
                                    {t("native.settings.showTimerDesc", {
                                        defaultValue: "Display a live timer while you play.",
                                    })}
                                </Typography>
                            </Box>
                            <Switch
                                checked={showPlayTimer}
                                onChange={handleShowPlayTimerChange}
                                inputProps={{
                                    "aria-label": t("native.settings.showTimer", {
                                        defaultValue: "Show timer",
                                    }),
                                }}
                            />
                        </Box>
                    </GroupedRow>
                    <GroupedRow>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontFamily: systemFont,
                                        fontSize: "0.9375rem",
                                        fontWeight: 600,
                                        color: tokens.text,
                                    }}
                                >
                                    {t("profile.settings.bgm-auto-play", {
                                        defaultValue: "Background music",
                                    })}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: systemFont,
                                        fontSize: "0.8125rem",
                                        lineHeight: 1.45,
                                        color: tokens.textMuted,
                                        mt: 0.5,
                                    }}
                                >
                                    {t("profile.settings.bgm-auto-play-desc", {
                                        defaultValue: "Play music automatically when you start a puzzle.",
                                    })}
                                </Typography>
                            </Box>
                            <Switch
                                checked={bgmAutoPlay}
                                onChange={handleBgmAutoPlayChange}
                                inputProps={{
                                    "aria-label": t("profile.settings.bgm-auto-play", {
                                        defaultValue: "Background music",
                                    }),
                                }}
                            />
                        </Box>
                    </GroupedRow>
                    <GroupedRow last={!showPushSettings}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontFamily: systemFont,
                                        fontSize: "0.9375rem",
                                        fontWeight: 600,
                                        color: tokens.text,
                                    }}
                                >
                                    {t("profile.settings.sound", {
                                        defaultValue: "Play sound effects",
                                    })}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: systemFont,
                                        fontSize: "0.8125rem",
                                        lineHeight: 1.45,
                                        color: tokens.textMuted,
                                        mt: 0.5,
                                    }}
                                >
                                    {t("profile.settings.sound-desc", {
                                        defaultValue:
                                            "Play sounds when you move tiles or complete a line.",
                                    })}
                                </Typography>
                            </Box>
                            <Switch
                                checked={playSoundEffects}
                                onChange={handlePlaySoundEffectsChange}
                                inputProps={{
                                    "aria-label": t("profile.settings.sound", {
                                        defaultValue: "Play sound effects",
                                    }),
                                }}
                            />
                        </Box>
                    </GroupedRow>
                    {showPushSettings ? (
                        <GroupedRow last>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 2,
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: systemFont,
                                            fontSize: "0.9375rem",
                                            fontWeight: 600,
                                            color: tokens.text,
                                        }}
                                    >
                                        {t("profile.settings.notifications", {
                                            defaultValue: "Notifications",
                                        })}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: systemFont,
                                            fontSize: "0.8125rem",
                                            lineHeight: 1.45,
                                            color: tokens.textMuted,
                                            mt: 0.5,
                                        }}
                                    >
                                        {t("native.settings.notificationsDesc", {
                                            defaultValue:
                                                "Get notified when a new puzzle is published.",
                                        })}
                                    </Typography>
                                </Box>
                                <Switch
                                    checked={notificationsEnabled}
                                    onChange={handleNotificationsChange}
                                    disabled={
                                        !isAuthenticated || accountLoading || notificationsSaving
                                    }
                                    inputProps={{
                                        "aria-label": t("profile.settings.notifications", {
                                            defaultValue: "Notifications",
                                        }),
                                    }}
                                />
                            </Box>
                        </GroupedRow>
                    ) : null}
                </GroupedSection>

                <GroupedSection
                    title={t("native.settings.appearanceSection", { defaultValue: "Appearance" })}
                >
                    <AppearancePicker />
                </GroupedSection>
            </GroupedList>

            <DeleteAccountConfirmDialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={() => {
                    setDeleteConfirmOpen(false);
                    setDataDeletionDialogOpen(true);
                }}
            />

            <DataDeletionRequestDialog
                open={dataDeletionDialogOpen}
                onClose={() => setDataDeletionDialogOpen(false)}
                onSubmit={handleDataDeletionRequest}
                submitting={dataDeletionSubmitting}
                hasActiveSubscription={hasStandardPlan}
                onSuccess={(deletionRequest) => {
                    navigate(ACCOUNT_DELETION_CONFIRMED_PATH, {
                        replace: true,
                        state: { deletionRequest },
                    });
                }}
            />

            <Snackbar
                open={iap.snackbar.open}
                autoHideDuration={iap.snackbar.persistent ? null : 6000}
                onClose={iap.hideSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={iap.hideSnackbar} severity={iap.snackbar.severity} sx={{ width: "100%" }}>
                    {iap.snackbar.message}
                </Alert>
            </Snackbar>
            </Box>
        </PageShell>
    );
}
